import React, { useCallback, useEffect } from 'react';
import {
  StudyType,
  useIsAdmin,
  useParticipation,
  usePrefetchParticipationResponse,
  useSession,
} from 'queries';
import {
  useMatch,
  useNavigate,
  useResolvedPath,
  useSearchParams,
} from 'react-router-dom';

import { ParticipationDetailsModal } from './ParticipationDetailsModal/ParticipationDetailsModal';
import { BookMeetingModal } from './ParticipationDetailsModal/BookMeetingModal';
import { CancelingModal } from './CancelingModal';
import { useParticipationIndexInColumn } from '../hooks/useParticipationIndexInColumn';
import { RescheduleMeetingModal } from './ParticipationDetailsModal/RescheduleMeetingModal';
import { MoveToOtherSessionModal } from './ParticipationDetailsModal/MoveToOtherSessionModal';
import { QuotaUpdateModal } from './ParticipationDetailsModal/QuotaUpdateModal';

interface Props {
  sessionId: string;
}

export enum Direction {
  PREVIOUS = 'PREVIOUS',
  NEXT = 'NEXT',
}

export const ParticipationDetailsModalPage = ({ sessionId }: Props) => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();

  const { pathname } = useResolvedPath('part/:id');
  const match = useMatch<'id', string>(`${pathname}/*`);
  const detailsOpened = useMatch(pathname);
  const bookingAppointmentModalOpened = useMatch(`${pathname}/book`);
  const rescheduleModalOpened = useMatch(`${pathname}/reschedule`);
  const cancelingModalOpened = useMatch(`${pathname}/cancel`);
  const quotaUpdateModalOpened = useMatch(`${pathname}/update-quota`);
  const moveToOtherSessionModalOpened = useMatch(`${pathname}/move`);
  const { data: session } = useSession(sessionId);

  const participationId = (match && match.params.id) || undefined;
  const { data: participation } = useParticipation(participationId);

  const [selectedIndex, participations] = useParticipationIndexInColumn(
    sessionId,
    participation,
  );
  const prefetchFormResponse = usePrefetchParticipationResponse();

  const canGoNext =
    selectedIndex !== -1 && selectedIndex < participations.length - 1;
  const canGoPrev = selectedIndex > 0;

  useEffect(() => {
    if (canGoPrev) {
      prefetchFormResponse(
        participations[selectedIndex - 1].id,
        participations[selectedIndex - 1].mainApplicationId,
      );
    }
    if (canGoNext) {
      prefetchFormResponse(
        participations[selectedIndex + 1].id,
        participations[selectedIndex + 1].mainApplicationId,
      );
    }
  }, [
    selectedIndex,
    participations,
    prefetchFormResponse,
    canGoPrev,
    canGoNext,
  ]);
  const closeDetailModal = useCallback(() => navigate(''), [navigate]);

  const openBookingModal = useCallback(
    () => navigate(`part/${participationId}/book`),
    [navigate, participationId],
  );

  const openRescheduleModal = useCallback(
    () => navigate(`part/${participationId}/reschedule`),
    [navigate, participationId],
  );

  const openCancelModal = useCallback(
    () => navigate(`part/${participationId}/cancel`),
    [navigate, participationId],
  );

  const openMoveToOtherSessionModal = useCallback(
    () => navigate(`part/${participationId}/move`),
    [navigate, participationId],
  );

  const closeSubModal = useCallback(
    (isCancelled?: boolean) => {
      if (isCancelled) {
        navigate(`part/${participationId}`);
      } else {
        navigate('');
      }
    },
    [navigate, participationId],
  );

  const onArrowClick = useCallback(
    (direction: Direction): void | undefined => {
      if (participation) {
        if (direction === 'NEXT') {
          if (!canGoNext) {
            return closeDetailModal();
          }
          const id = participations[selectedIndex + 1].id;
          return navigate(`part/${id}`);
        }

        if (direction === 'PREVIOUS') {
          if (!canGoPrev) {
            return closeDetailModal();
          }

          const id = participations[selectedIndex - 1].id;
          return navigate(`part/${id}`);
        }
      }
    },
    [
      participation,
      canGoNext,
      canGoPrev,
      participations,
      selectedIndex,
      closeDetailModal,
      navigate,
    ],
  );

  return (
    <>
      {session?.studyType === StudyType.MODERATED && (
        <>
          <BookMeetingModal
            participation={participation}
            isOpen={!!bookingAppointmentModalOpened}
            onRequestClose={closeSubModal}
          />
          <RescheduleMeetingModal
            participation={participation}
            isOpen={!!rescheduleModalOpened}
            onRequestClose={closeSubModal}
          />
          <CancelingModal
            isOpen={!!cancelingModalOpened}
            participation={participation}
            onRequestClose={closeSubModal}
          />
        </>
      )}
      <ParticipationDetailsModal
        key={`${participationId}-${searchParams.get('tab') || ''}`}
        isOpen={!!detailsOpened}
        onRequestClose={closeDetailModal}
        participation={participation}
        sessionId={sessionId}
        onArrowClick={onArrowClick}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrev}
        openBookingAppointmentModal={openBookingModal}
        reschedule={openRescheduleModal}
        cancel={openCancelModal}
        onRequestMoveToOtherSession={
          isAdmin ? openMoveToOtherSessionModal : undefined
        }
      />
      <QuotaUpdateModal
        participation={participation}
        isOpen={!!quotaUpdateModalOpened}
        onRequestClose={closeSubModal}
      />
      {!!participationId && isAdmin && (
        <MoveToOtherSessionModal
          participationId={participationId}
          isOpen={!!moveToOtherSessionModalOpened}
          onRequestClose={closeSubModal}
          organizationId={session?.organizationId}
          onSuccess={closeDetailModal}
        />
      )}
    </>
  );
};
